import React, { ReactNode } from "react";
import { Grid, CircularProgress, Theme, useTheme } from "@mui/material";

/**
 * The purpose of this component is to act as a progress symbol for whenever
 * the application is either making async calls or when it is initially starting up
 * @param classes: jss classes assigned to the component
 * @param isHeadingMsg: This determines if a heading style is applied to the content in loadingMsg.
 * @param loadingMsg: Message displayed on the loading spinner
 * @param children: Allows further custom multiline messages to be displayed as part of the AFLoadingSpinner.
 * @param isVisible: This determines rather or not the spinner is displayed.
 */
export type Props = {
  classes?: any;
  isHeadingMsg?: boolean;
  loadingMsg?: string;
  children?: ReactNode;
  isVisible?: boolean;
};

const AFLoadingSpinner = (props: Props): JSX.Element => {
  const theme: Theme = useTheme();

  const {  loadingMsg, children, isVisible } = props;

  return (
    <Grid container spacing={0} display={!!isVisible ? "block" : "none"}>
      <Grid item xs={12}>
        <div
          style={{
            backgroundColor: "white",
            background: "white",
            position: "fixed",
            zIndex: 8000,
            height: "100%",
            width: "100%",
            top: "0",
            left: "0",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            left: "0",
            top: "0",
            width: "100%",
            height: "100%",
            zIndex: 9999,
            overflow: "auto",
            animation: "loadingOpen 0.2s ease-in-out",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "40px 80px",
              borderRadius: "2px",
              border: `1px solid #FFFFFF`,
              pointerEvents: "all",
              [theme.breakpoints.down("sm")]: {
                padding: "40px 40px",
              },
            }}
          >
            <div />
            <CircularProgress
              variant="determinate"
              value={100}
              style={{
                color: "white",
                opacity: 0.25,
              }}
              size={80}
              thickness={4}
            />
            <CircularProgress
              variant="indeterminate"
              disableShrink
              style={{
                animationDuration: "0.75s",
                position: "absolute",
      
              }}
              classes={{
                circleIndeterminate: theme.custom.spinner.strokeDasharray,
              }}
              size={80}
              value={25}
              thickness={4}
            />
            <div
              style={{
                margin: "10px 0 0 8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  padding: "20px 0px",
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "1.25rem",
                  },
                }}
              >
                {loadingMsg}
              </p>
              {children}
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

AFLoadingSpinner.defaultProps = {
  loadingMsg: "Loading..",
  isVisible: true,
  useHeadingStyle: false,
};

export { AFLoadingSpinner };
